import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { executeAiManagedObject as executeAiManagedObjectRequest } from '@atlassian/ai-managed-objects/api';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import { pluginKey } from '../../common/agentManagedExtensionPluginType';
import { getExecutionLifecycleState } from '../../common/lifecycle';
import { findAgentManagedExtension, findAgentManagedExtensionByLocalId } from '../../common/utils';
import {
	dispatchAgentManagedExtensionMeta,
	replaceNodeBodyContent,
	setAgentManagedExtensionMeta,
} from '../editor-commands/agent-managed-object-node';
import { getCacheAiManagedObjectMeta } from '../editor-commands/configure-popup';

export type ExecuteAiManagedObjectArgs = {
	aimoId: string;
	localId?: string;
};

type ExecuteAiManagedObjectWithEditorViewArgs = ExecuteAiManagedObjectArgs & {
	editorView: EditorView;
};

let executionRequestSequence = 0;

const createExecutionRequestId = (): string => `execution-${++executionRequestSequence}`;

const getFinishExecutionMeta = ({ localId, requestId }: { localId?: string; requestId: string }) =>
	localId
		? {
				localId,
				requestId,
				type: 'FINISH_EXECUTION' as const,
			}
		: undefined;

const startExecution = ({
	editorView,
	lifecycleState,
	localId,
	requestId,
}: {
	editorView: EditorView;
	lifecycleState: ReturnType<typeof getExecutionLifecycleState>;
	localId?: string;
	requestId: string;
}): void => {
	if (!localId) {
		return;
	}

	dispatchAgentManagedExtensionMeta(editorView, {
		lifecycleState,
		localId,
		requestId,
		type: 'START_EXECUTION',
	});
};

const getCurrentLifecycleState = (
	editorView: EditorView,
	localId?: string,
): ReturnType<typeof getExecutionLifecycleState> => {
	const found =
		findAgentManagedExtensionByLocalId(editorView.state, localId) ??
		findAgentManagedExtension(editorView.state);

	return found ? getExecutionLifecycleState(found.node) : 'no-body-executing';
};

const isLatestExecutionRequest = ({
	editorView,
	localId,
	requestId,
}: {
	editorView: EditorView;
	localId: string;
	requestId: string;
}): boolean => {
	const pluginState = pluginKey.getState(editorView.state);
	if (!pluginState) {
		return true;
	}

	return pluginState.executionLifecycleByLocalId[localId]?.requestId === requestId;
};

export const executeAiManagedObject = async ({
	aimoId,
	editorView,
	localId,
}: ExecuteAiManagedObjectWithEditorViewArgs): Promise<AiManagedObject | null> => {
	const requestId = createExecutionRequestId();
	const lifecycleState = getCurrentLifecycleState(editorView, localId);
	startExecution({ editorView, lifecycleState, localId, requestId });
	try {
		const executionResult = await executeAiManagedObjectRequest(aimoId);

		if (editorView.isDestroyed || !localId) {
			return null;
		}

		if (!isLatestExecutionRequest({ editorView, localId, requestId })) {
			return null;
		}

		const executeTr = editorView.state.tr;
		replaceNodeBodyContent(
			editorView.state,
			executeTr,
			executionResult.output.blockContent,
			localId,
		);

		const cacheAiManagedObjectMeta = getCacheAiManagedObjectMeta(
			executionResult.aiManagedObject,
			localId,
		);
		const finishExecutionMeta = getFinishExecutionMeta({ localId, requestId });
		setAgentManagedExtensionMeta(
			executeTr,
			finishExecutionMeta
				? [cacheAiManagedObjectMeta, finishExecutionMeta]
				: cacheAiManagedObjectMeta,
		);

		editorView.dispatch(executeTr);

		return executionResult.aiManagedObject;
	} catch {
		if (editorView.isDestroyed) {
			return null;
		}

		if (localId && isLatestExecutionRequest({ editorView, localId, requestId })) {
			const failureTr = editorView.state.tr;
			setAgentManagedExtensionMeta(failureTr, [
				{
					localId,
					requestId,
					type: 'FAIL_EXECUTION',
				},
				{
					localId,
					requestId,
					type: 'FINISH_EXECUTION',
				},
			]);
			editorView.dispatch(failureTr);
		}
		return null;
	}
};
